import { useEffect } from 'react';
import { useFetcher } from 'react-router';

import {
    PRODUCT_NEIGHBOURS_API_ROUTE,
    type CategoryNeighbours,
    type loader as neighboursLoader,
} from '~/routes/remix-api.product.neighbours';

/**
 * The products either side of this one in its category listing, fetched on
 * arrival rather than carried by the page's own loader — the lookup reads the
 * category listing in hundreds and has no business on the page's first byte.
 *
 * Null until the answer lands, and null again the moment the product under
 * the page changes: the answer is marked with the product it was looked up
 * for, so the last page's neighbours are never laid out under this one's
 * foot while the new answer is on its way.
 */
export const useCategoryNeighbours = (
    categoryLink: string | undefined,
    productId: number,
): CategoryNeighbours | null => {
    const fetcher = useFetcher<typeof neighboursLoader>();
    const load = fetcher.load;

    useEffect(() => {
        if (!categoryLink) return;
        const params = new URLSearchParams({
            category: categoryLink,
            product: String(productId),
        });
        load(`${PRODUCT_NEIGHBOURS_API_ROUTE}?${params}`);
    }, [load, categoryLink, productId]);

    return fetcher.data && fetcher.data.for === productId ? fetcher.data : null;
};
